import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useDirty } from "../context/DirtyContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ScrollText, History, BookOpen, LogOut, Settings as SettingsIcon, MessageSquare, Bell } from "lucide-react";
import { useUnreadCounts, UnreadBadge } from "../hooks/useUnreadCounts";

export default function AppShell({ children }) {
    const { company, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { guardedRun } = useDirty();
    const { t } = useLang();
    const [confirmLogout, setConfirmLogout] = useState(false);
    const { chat: chatUnread, news: newsUnread } = useUnreadCounts();

    const go = (path) => guardedRun(() => navigate(path));

    /** Open the confirmation dialog before logging out. */
    const handleLogout = () => guardedRun(() => setConfirmLogout(true));

    /** Actually perform the logout after the user confirms. */
    const performLogout = async () => {
        setConfirmLogout(false);
        await logout();
        navigate("/login", { replace: true });
    };

    const isActive = (p) => location.pathname === p || (p !== "/" && location.pathname.startsWith(p));

    return (
        <div className="min-h-screen app-bg">
            <header className="sticky top-0 z-30 border-b border-neutral-200/70 bg-white/85 backdrop-blur">
                <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-3 sm:h-16 sm:px-6 lg:px-8">
                    <button
                        type="button"
                        onClick={() => go("/")}
                        className="flex min-w-0 items-center gap-2 sm:gap-2.5"
                        data-testid="brand-link"
                    >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-white sm:h-9 sm:w-9 sm:rounded-xl">
                            <ScrollText className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        <div className="min-w-0 text-start">
                            <div className="truncate font-heading text-sm font-bold leading-none sm:text-base">
                                {t.brand}
                            </div>
                            <div className="truncate text-[11px] text-neutral-500 sm:text-xs">
                                {company?.company_name}
                            </div>
                        </div>
                    </button>

                    <nav className="flex shrink-0 items-center gap-0.5 sm:gap-1">
                        <button
                            type="button"
                            onClick={() => go("/")}
                            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition sm:px-3 ${
                                location.pathname === "/"
                                    ? "bg-neutral-900 text-white"
                                    : "text-neutral-700 hover:bg-neutral-100"
                            }`}
                            data-testid="nav-ledger"
                        >
                            <BookOpen className="h-4 w-4" />
                            <span className="hidden sm:inline">{t.navLedger}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => go("/history")}
                            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition sm:px-3 ${
                                isActive("/history")
                                    ? "bg-neutral-900 text-white"
                                    : "text-neutral-700 hover:bg-neutral-100"
                            }`}
                            data-testid="nav-history"
                        >
                            <History className="h-4 w-4" />
                            <span className="hidden sm:inline">{t.navHistory}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => go("/support")}
                            className={`relative inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition sm:px-3 ${
                                isActive("/support")
                                    ? "bg-neutral-900 text-white"
                                    : "text-neutral-700 hover:bg-neutral-100"
                            }`}
                            data-testid="nav-support"
                            aria-label="المحادثات"
                        >
                            <MessageSquare className="h-4 w-4" />
                            <UnreadBadge count={chatUnread} testid="nav-support-badge" />
                        </button>
                        <button
                            type="button"
                            onClick={() => go("/whats-new")}
                            className={`relative inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition sm:px-3 ${
                                isActive("/whats-new")
                                    ? "bg-neutral-900 text-white"
                                    : "text-neutral-700 hover:bg-neutral-100"
                            }`}
                            data-testid="nav-whats-new"
                            aria-label="ما الجديد"
                        >
                            <Bell className="h-4 w-4" />
                            <UnreadBadge count={newsUnread} testid="nav-whats-new-badge" />
                        </button>
                        <button
                            type="button"
                            onClick={() => go("/settings")}
                            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition sm:px-3 ${
                                isActive("/settings")
                                    ? "bg-neutral-900 text-white"
                                    : "text-neutral-700 hover:bg-neutral-100"
                            }`}
                            data-testid="nav-settings"
                            aria-label={t.navSettings}
                        >
                            <SettingsIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">{t.navSettings}</span>
                        </button>
                        <Button
                            variant="ghost"
                            onClick={handleLogout}
                            className="ms-1 h-9 px-2.5 text-neutral-700 hover:bg-red-50 hover:text-red-700 sm:ms-2 sm:px-3"
                            data-testid="logout-btn"
                        >
                            <LogOut className="h-4 w-4 sm:ms-1" />
                            <span className="hidden sm:inline">{t.navLogout}</span>
                        </Button>
                    </nav>
                </div>
            </header>

            {/* Grace-period banner (#85 expired with grace) */}
            {company?.subscription_status === "grace" && company?.account_type !== "employee" ? (
                <div
                    className="border-b border-amber-300 bg-amber-100/80 px-3 py-2 text-center text-xs font-semibold text-amber-900 sm:px-6"
                    data-testid="grace-banner"
                >
                    ⚠️ {t.subGraceBanner}
                    {Number.isFinite(company.subscription_days_remaining)
                        ? ` — ${company.subscription_days_remaining} ${t.subDaysGrace}`
                        : ""}
                </div>
            ) : null}

            <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">{children}</main>

            <AlertDialog open={confirmLogout} onOpenChange={setConfirmLogout}>
                <AlertDialogContent data-testid="logout-confirm-dialog">
                    <AlertDialogHeader className="text-start">
                        <AlertDialogTitle className="flex items-center gap-2">
                            <LogOut className="h-5 w-5 text-red-700" />
                            {t.logoutConfirmTitle}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t.logoutConfirmBody}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-row-reverse gap-2 sm:flex-row-reverse">
                        <AlertDialogCancel data-testid="logout-confirm-cancel">
                            {t.cancel}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); performLogout(); }}
                            className="bg-red-700 text-white hover:bg-red-800"
                            data-testid="logout-confirm-ok"
                        >
                            <LogOut className="me-1 h-4 w-4" />
                            {t.logoutConfirmOk}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
